import React, { useMemo } from 'react';

import { useSnackbar } from 'notistack';

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';

import { useCloneDealPage } from '~/hooks/apis/cruises/useCloneDealPage';
import { useGetDealPage } from '~/hooks/apis/cruises/useGetDealPage';
import useAppNavigation from '~/hooks/useAppNavigation';

import { formatDateLongMonthWithMeridiemNoSeconds } from '~/services/TimeService';

const MAX_INCLUSIONS_PREVIEW = 3;

interface Props {
  dealId: string;
  open: boolean;
  onClose: () => void;
  onCloneSuccess: () => void;
}

function DealPageCloneConfirmation({ dealId, open, onClose, onCloneSuccess }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { push } = useAppNavigation();

  const { data, isLoading: isLoadingDeal } = useGetDealPage(dealId, open);
  const dealPage = data?.result;

  const cloneMutation = useCloneDealPage();

  const inclusionsPreview = useMemo(
    () => (dealPage?.inclusions || []).sort((a, b) => a.order - b.order).slice(0, MAX_INCLUSIONS_PREVIEW),
    [dealPage?.inclusions],
  );

  const hasFilters = (dealPage?.rates?.length ?? 0) > 0 || (dealPage?.departures?.length ?? 0) > 0;

  const handleConfirm = () => {
    cloneMutation.mutate(dealId, {
      onSuccess: (clonedDeal) => {
        onClose();
        onCloneSuccess();
        enqueueSnackbar('Deal page cloned successfully', { variant: 'success' });
        push(`/cruises/deals/${clonedDeal.id}/edit`);
      },
      onError: () => {
        enqueueSnackbar('Error cloning deal page', { variant: 'error' });
      },
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Clone Deal Page</DialogTitle>
      <DialogContent>
        {isLoadingDeal && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={32} />
          </Box>
        )}

        {dealPage && (
          <>
            <Typography variant="body2" color="text.secondary" mb={2}>
              A new deal page will be created with the following data:
            </Typography>

            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Name
                </Typography>
                <Typography variant="body1">{dealPage.name}</Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Cruise Line
                </Typography>
                <Typography variant="body1">{dealPage.cruiseLine.name}</Typography>
              </Box>

              <Stack direction="row" spacing={4}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Start Date
                  </Typography>
                  <Typography variant="body1">
                    {formatDateLongMonthWithMeridiemNoSeconds(dealPage.startDate)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    End Date
                  </Typography>
                  <Typography variant="body1">{formatDateLongMonthWithMeridiemNoSeconds(dealPage.endDate)}</Typography>
                </Box>
              </Stack>

              {hasFilters && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Filters
                    </Typography>
                    <Stack direction="row" spacing={1} mt={0.5} flexWrap="wrap" useFlexGap>
                      {dealPage.rates.map((rate) => (
                        <Chip key={rate.id} label={`Rate: ${rate.code}`} size="small" variant="outlined" />
                      ))}
                      {dealPage.departures.map((departure) => (
                        <Chip
                          key={departure.id}
                          label={`Departure: ${departure.name}`}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  </Box>
                </>
              )}

              {inclusionsPreview.length > 0 && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Inclusions (first {MAX_INCLUSIONS_PREVIEW})
                    </Typography>
                    <Stack spacing={0.5} mt={0.5}>
                      {inclusionsPreview.map((item, index) => (
                        <Typography key={item.id} variant="body2">
                          {index + 1}. {item.description}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                </>
              )}
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={cloneMutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isLoadingDeal || !dealPage || cloneMutation.isPending}
        >
          {cloneMutation.isPending && <CircularProgress size={20} sx={{ mr: 1 }} />}
          Confirm Clone
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DealPageCloneConfirmation;
